/**
 * ATTESTERA — chefens kö. Prototypens data-screen="attest".
 *
 * EN VECKA, ALDRIG EN MÅNAD (beslut 04). Allt på skärmen hänger på EN IsoWeek:
 * kön, summan i toppen, knapparna och varje operation som skickas. Månaden finns
 * inte i tidrapporteringen och det finns ingen operation som låser en månad.
 *
 * KNAPPARNA HÄRLEDS UR KONTRAKTET, INTE UR EN LISTA HÄR. WEEK_TRANSITIONS är
 * DATA (shared/routes.ts): 'approve' går från open/submitted/reopened, 'lock'
 * bara från approved, 'reopen' från approved/locked (beslut 19). `tillaten()`
 * nedan slår upp i den tabellen, så en ändring i kontraktet ändrar knapparna i
 * samma andetag. Skriver man i stället av reglerna hit börjar gränssnittet
 * erbjuda övergångar servern svarar WEEK_TRANSITION_INVALID på.
 *
 * CHEFENS EGEN TID STÅR UTANFÖR KÖN (beslut 10). När en manager sparar en rad
 * där han själv är account_id och hans egen vecka står i 'open' sätter SERVERN
 * veckan direkt till 'approved' — den går aldrig genom attesten. Skärmen visar
 * den därför i en EGEN, TYST yta med en mening som säger varför. Det är inte ett
 * fel, inte en varning och inte något som fattas: det är så produkten fungerar,
 * och veckan går ändå att låsa när allt annat är klart.
 *
 * VISA MED kanSeLaget, LÅTA GÖRA MED arChef (app/session.tsx). Bokföraren står i
 * FULL_VIEW_ROLES och SER hela kön, men han förekommer inte i EN ENDA rad i
 * SYNC_RULES (beslut 07) — 'week:approve', 'week:lock' och 'week:reopen' är alla
 * MANAGER_ONLY. Hans knappar FINNS därför inte, de är inte inaktiverade.
 *
 * LÅSLÄGET STÄNGER SKRIVNINGEN, INTE LÄSNINGEN (beslut 11): SYNC_PUSH är
 * locked_mode 'blocked', så knapparna är av medan kön fortsätter synas.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { SYNC_PUSH, WEEK_TRANSITIONS } from '@delat/routes';
import { MAX_OPS_PER_PUSH, asMillis } from '@delat/types';
import type {
  AbsenceRow,
  AccountId,
  ExpenseRow,
  IsoWeek,
  MemberRow,
  MileageRow,
  OpId,
  Role,
  SyncOp,
  TimeEntryRow,
  WeekState,
  WeekTransition,
  WorkDate,
} from '@delat/types';
import { granska, klienten, enhetsId, nyUlid } from '../api';
import type { Klartidfel } from '../api';
import { granskaSyncPush, somKlartidfel } from '../api/granskare/laget';
import { useInloggad } from '../app/session';
import {
  arHelg,
  dagarIVecka,
  dagarMellan,
  idag,
  plusVeckor,
  veckanNu,
  visaDag,
  visaMinuter,
  visaMil,
  visaOren,
} from '../app/datum';
import {
  Felruta,
  Installningsrad,
  Knapp,
  Laddar,
  Personrad,
  Tomtext,
  Veckonavigering,
  Yta,
  initialer,
} from '../ui';
import { useFirmadata, visaDagar } from './LagetData';

/* == Veckans läge som text ================================================ */

const LAGETEXT: Readonly<Record<WeekState, string>> = {
  open: 'Inte inlämnad',
  submitted: 'Inlämnad',
  approved: 'Godkänd',
  locked: 'Låst',
  reopened: 'Öppnad igen',
};

/** Kön först: det som väntar på chefen ska stå överst i varje yta. */
const BRADSKA: Readonly<Record<WeekState, number>> = {
  submitted: 0,
  reopened: 1,
  open: 2,
  approved: 3,
  locked: 4,
};

/**
 * Får övergången göras från det läge veckan FAKTISKT står i? Frågan ställs till
 * WEEK_TRANSITIONS och besvaras aldrig av en avskrift. Casten vidgar bara en
 * `readonly ['open', ...]` till `readonly WeekState[]` så att includes() går att
 * anropa med en WeekState — den byter ingen typ, den släpper bara fram frågan.
 */
const tillaten = (overgang: WeekTransition, lage: WeekState): boolean =>
  (WEEK_TRANSITIONS[overgang].from as readonly WeekState[]).includes(lage);

/* == En rad i kön ========================================================= */

interface Korad {
  readonly medlem: MemberRow;
  readonly lage: WeekState;
  readonly minuter: number;
  readonly dagar: number;
  readonly meter: number;
  readonly utlaggOren: number;
  readonly franvarodagar: number;
  readonly saknade: readonly WorkDate[];
  /** Finns det något att attestera? En tom vecka har ingenting att godkänna. */
  readonly harNagot: boolean;
}

const ANSTALLDA: readonly Role[] = ['manager', 'worker', 'apprentice'];

const inom = (dag: WorkDate, dagar: readonly WorkDate[]): boolean => dagar.includes(dag);

/** "torsdag" ur datum.ts egen formatering — ingen andra veckodagstabell. */
const veckodagsnamn = (dag: WorkDate): string =>
  (visaDag(dag).split(' ')[0] ?? '').toLowerCase();

const byggRad = (
  medlem: MemberRow,
  lage: WeekState,
  dagarna: readonly WorkDate[],
  tidrader: readonly TimeEntryRow[],
  milrader: readonly MileageRow[],
  utlagg: readonly ExpenseRow[],
  franvaro: readonly AbsenceRow[],
  harVeckorad: boolean,
): Korad => {
  const mina = tidrader.filter((t) => t.account_id === medlem.account_id);
  const minaDagar = new Set(mina.map((t) => t.work_date));
  const franvarodagar = new Set(
    franvaro.filter((f) => f.account_id === medlem.account_id).map((f) => f.work_date),
  );
  const minuter = mina.reduce((s, t) => s + Number(t.minutes), 0);
  const meter = milrader
    .filter((m) => m.account_id === medlem.account_id)
    .reduce((s, m) => s + Number(m.meters), 0);
  const utlaggOren = utlagg
    .filter((u) => u.account_id === medlem.account_id)
    .reduce((s, u) => s + Number(u.amount_ore), 0);

  const nu = idag();
  const saknade = dagarna.filter(
    (d) =>
      !arHelg(d) &&
      dagarMellan(d, nu) >= 0 &&
      !minaDagar.has(d) &&
      !franvarodagar.has(d),
  );

  return {
    medlem,
    lage,
    minuter,
    dagar: minaDagar.size,
    meter,
    utlaggOren,
    franvarodagar: franvarodagar.size,
    saknade,
    harNagot: minuter > 0 || meter > 0 || utlaggOren > 0 || harVeckorad,
  };
};

/** "Inlämnad · 5 dagar · 12,5 mil · 340 kr utlägg · torsdag saknas" */
const undertext = (rad: Korad): string => {
  const delar: string[] = [LAGETEXT[rad.lage]];
  if (rad.dagar > 0) delar.push(visaDagar(rad.dagar));
  if (rad.meter > 0) delar.push(visaMil(rad.meter));
  // EX MOMS STÅR DÄR BELOPPET STÅR. ExpenseRow.amount_ore är montörens egen
  // summa, skriven EX MOMS — och varje annan skärm i appen skriver ut det.
  if (rad.utlaggOren > 0) delar.push(`${visaOren(rad.utlaggOren)} utlägg ex moms`);
  if (rad.franvarodagar > 0) delar.push(`${visaDagar(rad.franvarodagar)} frånvaro`);
  const forsta = rad.saknade[0];
  if (rad.saknade.length === 1 && forsta !== undefined) {
    delar.push(`${veckodagsnamn(forsta)} saknas`);
  } else if (rad.saknade.length > 1) {
    delar.push(`${visaDagar(rad.saknade.length)} saknas`);
  }
  if (rad.dagar === 0 && rad.meter === 0 && rad.utlaggOren === 0) {
    delar.push('inget rapporterat');
  }
  return delar.join(' · ');
};

/* == Skärmen ============================================================== */

interface Onskan {
  readonly account_id: AccountId;
  readonly transition: WeekTransition;
}

export const Attestera = () => {
  const { firmaId, konto, arChef, arLast } = useInloggad();
  const [vecka, sattVecka] = useState<IsoWeek>(() => veckanNu());
  const [omgang, sattOmgang] = useState(0);
  const [arbetar, sattArbetar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const lage = useFirmadata(firmaId, omgang);

  /**
   * BESLUT 09: op_id myntas när användaren TRYCKER och ändras aldrig. En
   * omsändning av samma önskan — efter ett tappat nät, efter "Försök igen" —
   * ska bära samma nyckel, annars blir varje försök en ny skrivning. Nyckeln
   * ryms i (person, vecka, övergång): det är exakt den handling användaren bad
   * om. Posten städas när servern kvitterat den.
   */
  const opIdn = useRef<Map<string, OpId>>(new Map());

  const opIdFor = useCallback((nyckel: string): OpId => {
    const fanns = opIdn.current.get(nyckel);
    if (fanns !== undefined) return fanns;
    const nytt = granska.opId(nyUlid(), 'op_id');
    opIdn.current.set(nyckel, nytt);
    return nytt;
  }, []);

  const kor = useCallback(
    (onskningar: readonly Onskan[]): void => {
      if (onskningar.length === 0 || arbetar) return;
      sattArbetar(true);
      sattFel(null);

      void (async () => {
        const ops: SyncOp[] = onskningar.map((o) => ({
          op_id: opIdFor(`${o.account_id}|${vecka}|${o.transition}`),
          client_time: asMillis(Date.now()),
          kind: 'week',
          account_id: o.account_id,
          iso_week: vecka,
          transition: o.transition,
        }));

        let forstaFel: Klartidfel | null = null;
        // MAX_OPS_PER_PUSH är kuvertets tak; över det faller HELA anropet med
        // TOO_MANY_OPS. En firma med 200 personer är inte Klartids kund, men
        // delningen kostar tre rader och gör gränsen till serverns och inte
        // till en gissning här.
        for (let i = 0; i < ops.length; i += MAX_OPS_PER_PUSH) {
          const kuvert = ops.slice(i, i + MAX_OPS_PER_PUSH);
          const svar = await klienten.anrop(
            SYNC_PUSH,
            { params: { company_id: firmaId }, data: { device_id: enhetsId(), ops: kuvert } },
            granskaSyncPush,
          );
          if (!svar.ok) {
            // HELANROPSFEL rör aldrig utkorgen (shared/errors.ts): op_id:na
            // ligger kvar i kartan och samma tryck skickar samma nycklar igen.
            forstaFel = svar.fel;
            break;
          }
          for (const resultat of svar.varde.results) {
            if (resultat.status === 'failed') {
              if (forstaFel === null) forstaFel = somKlartidfel(resultat.error);
              continue;
            }
            // Kvitterad: nyckeln får inte återanvändas för en NY handling.
            for (const [nyckel, id] of opIdn.current) {
              if (id === resultat.op_id) opIdn.current.delete(nyckel);
            }
          }
        }

        sattFel(forstaFel);
        sattArbetar(false);
        // Push-svaret bär aldrig radinnehåll — klienten hämtar alltid in på nytt.
        sattOmgang((n) => n + 1);
      })();
    },
    [arbetar, firmaId, opIdFor, vecka],
  );

  const dagarna = useMemo(() => dagarIVecka(vecka), [vecka]);

  const rader = useMemo<readonly Korad[]>(() => {
    if (lage.status !== 'klar') return [];
    const d = lage.data;
    const tid = d.tidrader.filter((t) => inom(t.work_date, dagarna));
    const mil = d.milrader.filter((m) => inom(m.work_date, dagarna));
    const utl = d.utlagg.filter((u) => inom(u.work_date, dagarna));
    const fra = d.franvaro.filter((f) => inom(f.work_date, dagarna));
    const veckolagen = new Map(
      d.veckolagen
        .filter((v) => v.iso_week === vecka)
        .map((v) => [v.account_id as string, v.state]),
    );

    return d.medlemmar
      .filter((m) => m.role !== 'bookkeeper')
      .map((m) => {
        const sparat = veckolagen.get(m.account_id);
        return byggRad(
          m,
          sparat ?? 'open',
          dagarna,
          tid,
          mil,
          utl,
          fra,
          sparat !== undefined,
        );
      })
      .sort(
        (a, b) =>
          BRADSKA[a.lage] - BRADSKA[b.lage] ||
          a.medlem.name.localeCompare(b.medlem.name, 'sv'),
      );
  }, [lage, dagarna, vecka]);

  const egen = arChef ? rader.find((r) => r.medlem.account_id === konto.id) ?? null : null;
  const ovriga = rader.filter((r) => r !== egen);
  const anstallda = ovriga.filter((r) => ANSTALLDA.includes(r.medlem.role));
  const inhyrda = ovriga.filter((r) => r.medlem.role === 'guest');

  const veckansMinuter = rader.reduce((s, r) => s + r.minuter, 0);
  const attGodkanna = ovriga.filter((r) => r.harNagot && tillaten('approve', r.lage));
  const attLasa = rader.filter((r) => tillaten('lock', r.lage));

  const knappFor = (rad: Korad): ReactNode => {
    if (!arChef) return undefined;
    const val: { readonly overgang: WeekTransition; readonly etikett: string }[] = [];
    if (rad.harNagot && tillaten('approve', rad.lage)) {
      val.push({ overgang: 'approve', etikett: 'Godkänn' });
    }
    if (tillaten('lock', rad.lage)) val.push({ overgang: 'lock', etikett: 'Lås' });
    if (tillaten('reopen', rad.lage)) val.push({ overgang: 'reopen', etikett: 'Öppna igen' });
    if (val.length === 0) return undefined;
    return (
      <>
        {val.map((v, i) => (
          <Knapp
            key={v.overgang}
            variant={i === 0 ? 'piller' : 'piller-mini'}
            disabled={arLast || arbetar}
            {...(arLast ? { title: 'Prenumerationen är pausad. Inget nytt kan sparas.' } : {})}
            onClick={() =>
              kor([{ account_id: rad.medlem.account_id, transition: v.overgang }])
            }
          >
            {v.etikett}
          </Knapp>
        ))}
      </>
    );
  };

  const lista = (rad: Korad): ReactNode => (
    <Personrad
      key={rad.medlem.account_id}
      initialer={initialer(rad.medlem.name)}
      namn={rad.medlem.name}
      under={undertext(rad)}
      varde={visaMinuter(rad.minuter)}
      atgard={knappFor(rad)}
    />
  );

  return (
    <>
      <Veckonavigering
        vecka={vecka}
        summa={lage.status === 'klar' ? visaMinuter(veckansMinuter) : undefined}
        onForegaende={() => sattVecka((v) => plusVeckor(v, -1))}
        onNasta={() => sattVecka((v) => plusVeckor(v, 1))}
      />

      {fel !== null && (
        <Felruta
          fel={fel}
          atgard={
            <Knapp variant="piller-mini" onClick={() => sattFel(null)}>
              Stäng
            </Knapp>
          }
        />
      )}

      {lage.status === 'laddar' && <Laddar rader={4} />}

      {lage.status === 'fel' && (
        <Felruta
          fel={lage.fel}
          atgard={
            <Knapp variant="piller" onClick={() => sattOmgang((n) => n + 1)}>
              Försök igen
            </Knapp>
          }
        />
      )}

      {lage.status === 'klar' && (
        <>
          {egen !== null && (
            <Yta rubrik="Din egen vecka" rakning={visaMinuter(egen.minuter)} ikon="tid" tyst>
              {/* "Läget" — veckans tillstånd, inte laget som i personalen. */}
              <Installningsrad
                ikon="attest"
                etikett="Läget"
                varde={LAGETEXT[egen.lage]}
                atgard={knappFor(egen)}
              />
              <Tomtext>
                Din tid godkänns i samma stund du sparar den, så du står aldrig i kön
                nedan. Veckan går ändå att låsa när allt annat är klart.
              </Tomtext>
            </Yta>
          )}

          <Yta rubrik="Anställda" rakning={`${anstallda.length} st`}>
            {anstallda.length === 0 ? (
              <Tomtext>Ingen anställd är upplagd i firman än.</Tomtext>
            ) : (
              anstallda.map(lista)
            )}
          </Yta>

          {inhyrda.length > 0 && (
            <Yta rubrik="Inhyrda" rakning="vidarefaktureras">
              {inhyrda.map(lista)}
            </Yta>
          )}

          {arChef && attGodkanna.length > 0 && (
            <div style={{ marginTop: '0.8rem' }}>
              <Knapp
                disabled={arLast || arbetar}
                {...(arLast
                  ? { title: 'Prenumerationen är pausad. Inget nytt kan sparas.' }
                  : {})}
                onClick={() =>
                  kor(
                    attGodkanna.map((r) => ({
                      account_id: r.medlem.account_id,
                      transition: 'approve' as const,
                    })),
                  )
                }
              >
                {attGodkanna.length === 1
                  ? 'Godkänn veckan för en person'
                  : `Godkänn ${attGodkanna.length} veckor`}
              </Knapp>
            </div>
          )}

          {arChef && attLasa.length > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              <Knapp
                variant="stor-tyst"
                disabled={arLast || arbetar}
                {...(arLast
                  ? { title: 'Prenumerationen är pausad. Inget nytt kan sparas.' }
                  : {})}
                onClick={() =>
                  kor(
                    attLasa.map((r) => ({
                      account_id: r.medlem.account_id,
                      transition: 'lock' as const,
                    })),
                  )
                }
              >
                {`Lås veckan (${attLasa.length} godkända)`}
              </Knapp>
            </div>
          )}

          <Tomtext>
            {arChef
              ? 'Attesterad tid blir fakturaunderlag samma sekund. En låst vecka kan du öppna igen om något ska rättas.'
              : 'Du läser allt och ändrar ingenting. Attest, lås och återöppning gör chefen.'}
          </Tomtext>
        </>
      )}
    </>
  );
};
